import { useCallback, useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle2, AlertTriangle, Loader2, Lock, LockOpen } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  closeMonth, listPeriodClosures, reopenMonth, money, type PeriodClosure,
} from "@/lib/financialLedger";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onDone: () => void;
}

const defaultMonth = () => {
  const d = new Date();
  d.setUTCMonth(d.getUTCMonth() - 1);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
};

export function CloseMonthDialog({ open, onOpenChange, onDone }: Props) {
  const { toast } = useToast();
  const [month, setMonth] = useState(defaultMonth());
  const [notes, setNotes] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState<{ message: string; unresolved: number } | null>(null);
  const [closures, setClosures] = useState<PeriodClosure[]>([]);

  const refresh = useCallback(async () => {
    try {
      const r = await listPeriodClosures();
      setClosures(r.closures);
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => {
    if (!open) { setBlocked(null); setConfirm(false); setNotes(""); return; }
    refresh();
  }, [open, refresh]);

  const submit = async (force = false) => {
    if (!confirm) {
      toast({ title: "Confirmation required", description: "Tick the confirmation box to close this month.", variant: "destructive" });
      return;
    }
    setBusy(true); setBlocked(null);
    try {
      const res = await closeMonth(month, { notes: notes || undefined, force });
      toast({
        title: "Month closed",
        description: `${res.closure.period_label} is now read-only. Later changes must be recorded as adjustments.`,
      });
      setNotes(""); setConfirm(false);
      await refresh();
      onDone();
    } catch (e) {
      const msg = (e as Error).message;
      setBlocked({ message: msg, unresolved: Number(msg.match(/(\d+) unresolved/)?.[1] ?? 0) });
    } finally {
      setBusy(false);
    }
  };

  const reopen = async (c: PeriodClosure) => {
    const reason = window.prompt(`Reopening ${c.period_label} is recorded permanently. Why is it being reopened?`);
    if (!reason) return;
    try {
      await reopenMonth(c.id, reason);
      toast({ title: "Month reopened", description: "The reopening is recorded in the financial audit log." });
      await refresh();
      onDone();
    } catch (e) {
      toast({ title: "Could not reopen", description: (e as Error).message, variant: "destructive" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Close a month</DialogTitle>
          <DialogDescription>
            Closing a month freezes its totals, records who closed it and when, and makes those records read-only.
            History is never overwritten — later corrections are recorded as adjustment or reversal transactions.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label className="text-xs">Month</Label>
              <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Closing notes (optional)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              placeholder="Anything a future reader should know about this month." />
          </div>
          <label className="flex items-start gap-2 text-sm">
            <Checkbox checked={confirm} onCheckedChange={(v) => setConfirm(v === true)} />
            <span>
              I confirm these figures are final. After closing, this month becomes read-only.
            </span>
          </label>

          {blocked && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Not ready to close</AlertTitle>
              <AlertDescription className="space-y-2">
                <p>{blocked.message}</p>
                <Button size="sm" variant="outline" onClick={() => submit(true)} disabled={busy}>
                  Close anyway and record the unresolved items
                </Button>
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <p className="text-sm font-medium">Closed periods</p>
            {closures.length === 0 && <p className="text-xs text-muted-foreground">No months have been closed yet.</p>}
            {closures.map((c) => (
              <div key={c.id} className="rounded-lg border border-border p-3 space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    {c.status === "closed"
                      ? <Lock className="h-4 w-4 text-primary" />
                      : <LockOpen className="h-4 w-4 text-amber-600" />}
                    <p className="text-sm font-medium">{c.period_label}</p>
                    <Badge variant={c.status === "closed" ? "secondary" : "outline"}>
                      {c.status === "closed" ? "Closed" : "Reopened"}
                    </Badge>
                    {c.discrepancy_count === 0
                      ? <Badge variant="secondary" className="gap-1"><CheckCircle2 className="h-3 w-3" /> Reconciled</Badge>
                      : <Badge variant="destructive">{c.discrepancy_count} unresolved</Badge>}
                  </div>
                  {c.status === "closed" && (
                    <Button size="sm" variant="ghost" onClick={() => reopen(c)}>Reopen</Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Closed by {c.closed_by_label ?? "Administrator"} on {new Date(c.closed_at).toLocaleString("en-US")}
                </p>
                <p className="text-xs text-muted-foreground">
                  Revenue {money(Number(c.totals?.actual_revenue_cents ?? 0))} ·
                  {" "}Expenses {money(Number(c.totals?.actual_expenses_cents ?? 0))} ·
                  {" "}Net {money(Number(c.totals?.net_cash_activity_cents ?? 0))}
                </p>
                {c.notes && <p className="text-xs text-muted-foreground">{c.notes}</p>}
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close window</Button>
          <Button onClick={() => submit(false)} disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Lock className="h-4 w-4 mr-1" />}
            Close month
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
